/**
 * BinDirective - Large, prominent bin assignment display
 *
 * Shows where to place the garment:
 * - PACKING_SHELF: Single-line order, no bin needed
 *   (with `handover`: the order is for a client set to Hand over, so it goes
 *   to the hand over bench instead and never gets a label)
 * - PLACE_IN_BIN: Multi-line with existing bin
 * - ASSIGN_BIN: Multi-line order needs bin (click Accept to assign)
 * - ASSIGN_BENCH (BLU-197): B2B/large order needs a bench scanned now
 * - PLACE_ON_BENCH (BLU-197): order already has a bench assigned
 * - isAssigning: Actively assigning bin (loading state)
 *
 * Phase 5.2: Added breathing animation for PLACE_IN_BIN
 */
import { useTranslation } from 'react-i18next';
import { Package, Archive, Loader2, BoxSelect, LayoutPanelTop, ScanLine, HandHelping } from 'lucide-react';
import type { GarmentLookupResult } from '../hooks/useGarmentLookup';
import { cn } from '@/lib/utils';

interface BinDirectiveProps {
  directive: GarmentLookupResult['binDirective'];
  isAssigning?: boolean;
}

export function BinDirective({ directive, isAssigning = false }: BinDirectiveProps) {
  const { t } = useTranslation('production');

  if (directive.action === 'PACKING_SHELF' && directive.handover) {
    // Teal, its own colour: this garment must NOT go to the ordinary packing
    // shelf, so it has to read differently at a glance.
    return (
      <div className="flex items-center justify-center gap-4 p-6 bg-teal-50 dark:bg-teal-950/30 border-2 border-teal-300 dark:border-teal-700 rounded-xl">
        <HandHelping className="h-10 w-10 text-teal-600 dark:text-teal-400" aria-hidden="true" />
        <div className="text-center">
          <p className="text-2xl font-bold text-teal-700 dark:text-teal-300">
            {t('binDirective.sendToHandoverBench')}
          </p>
          <p className="text-sm text-teal-600 dark:text-teal-400">
            {t('binDirective.handoverNoLabel')}
          </p>
        </div>
      </div>
    );
  }

  if (directive.action === 'PACKING_SHELF') {
    return (
      <div className="flex items-center justify-center gap-4 p-6 bg-green-50 dark:bg-green-950/30 border-2 border-green-200 dark:border-green-800 rounded-xl">
        <Package className="h-10 w-10 text-green-600 dark:text-green-400" />
        <div className="text-center">
          <p className="text-2xl font-bold text-green-700 dark:text-green-300">
            {t('binDirective.sendToPackingShelf')}
          </p>
          <p className="text-sm text-green-600 dark:text-green-400">
            {t('binDirective.singleItemOrder')}
          </p>
        </div>
      </div>
    );
  }

  // Show spinner only when actively assigning (mutation in progress).
  // BLU-197: a bench claim shows indigo "Assigning bench…" instead of the amber
  // bin spinner so the operator gets the right semantic colour + copy.
  if (isAssigning) {
    const benchAssigning = directive.action === 'ASSIGN_BENCH' || directive.action === 'PLACE_ON_BENCH';
    return benchAssigning ? (
      <div className="flex items-center justify-center gap-4 p-6 bg-indigo-50 dark:bg-indigo-950/30 border-2 border-indigo-200 dark:border-indigo-800 rounded-xl">
        <Loader2 className="h-10 w-10 text-indigo-600 dark:text-indigo-400 animate-spin" />
        <div className="text-center">
          <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
            {t('binDirective.assigningBench')}
          </p>
          <p className="text-sm text-indigo-600 dark:text-indigo-400">
            {t('binDirective.claimingBench')}
          </p>
        </div>
      </div>
    ) : (
      <div className="flex items-center justify-center gap-4 p-6 bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-200 dark:border-amber-800 rounded-xl">
        <Loader2 className="h-10 w-10 text-amber-600 dark:text-amber-400 animate-spin" />
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
            {t('binDirective.assigningBin')}
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            {t('binDirective.findingBin')}
          </p>
        </div>
      </div>
    );
  }

  // BLU-197: ASSIGN_BENCH — order needs a bench claimed now. The operator scans a
  // bench QR (not the Accept button); indigo distinguishes it from a bin (amber).
  if (directive.action === 'ASSIGN_BENCH') {
    return (
      <div className="flex items-center justify-center gap-4 p-6 bg-indigo-50 dark:bg-indigo-950/30 border-2 border-indigo-200 dark:border-indigo-800 rounded-xl">
        <ScanLine className="h-10 w-10 text-indigo-600 dark:text-indigo-400" />
        <div className="text-center">
          <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300">
            {t('binDirective.scanABench')}
          </p>
          <p className="text-sm text-indigo-600 dark:text-indigo-400">
            {t('binDirective.scanABenchDesc')}
          </p>
        </div>
      </div>
    );
  }

  // BLU-197: PLACE_ON_BENCH — order already has a bench; preview where it goes.
  if (directive.action === 'PLACE_ON_BENCH') {
    return (
      <div
        className={cn(
          'flex items-center justify-center gap-4 p-6 bg-indigo-50 dark:bg-indigo-950/30 border-2 border-dashed border-indigo-300 dark:border-indigo-700 rounded-xl',
        )}
      >
        <LayoutPanelTop className="h-10 w-10 text-indigo-600 dark:text-indigo-400" />
        <div className="text-center">
          <p className="text-xs uppercase tracking-wider text-indigo-500 dark:text-indigo-400 mb-0.5">{t('binDirective.placeOnBench')}</p>
          <p className="text-4xl font-bold text-indigo-700 dark:text-indigo-300 tracking-wide">
            {directive.binLabel || 'BENCH'}
          </p>
        </div>
      </div>
    );
  }

  // Show "needs assignment" state when ASSIGN_BIN but not actively assigning
  if (directive.action === 'ASSIGN_BIN') {
    return (
      <div className="flex items-center justify-center gap-4 p-6 bg-amber-50 dark:bg-amber-950/30 border-2 border-amber-200 dark:border-amber-800 rounded-xl">
        <BoxSelect className="h-10 w-10 text-amber-600 dark:text-amber-400" />
        <div className="text-center">
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">
            {t('binDirective.binWillBeAssigned')}
          </p>
          <p className="text-sm text-amber-600 dark:text-amber-400">
            {t('binDirective.clickAccept')}
          </p>
        </div>
      </div>
    );
  }

  // PLACE_IN_BIN - Preview of where garment will go after accepting
  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4 p-6 bg-blue-50 dark:bg-blue-950/30 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-xl',
      )}
    >
      <Archive className="h-10 w-10 text-blue-600 dark:text-blue-400" />
      <div className="text-center">
        <p className="text-xs uppercase tracking-wider text-blue-500 dark:text-blue-400 mb-0.5">{t('binDirective.placeIn')}</p>
        <p className="text-4xl font-bold text-blue-700 dark:text-blue-300 tracking-wide">
          {directive.binLabel || 'BIN'}
        </p>
      </div>
    </div>
  );
}
